import React from 'react'

import { Dropdown, useModalContext } from '@sketch/components'
import { useFlag, useFolderTerm } from '@sketch/modules-common'
import {
  ProjectFragment,
  ProjectListItemFragment,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

import {
  CopyProjectAction,
  CreateCollectionAction,
  CreateNestedProjectAction,
  DeleteProjectAction,
  LeaveProjectAction,
  EditProjectAction,
  RenameProjectAction,
  ShareProjectAction,
  PinProjectAction,
  MoveProjectAction,
} from '../ProjectDropdownActions'

import ArchiveProjectModal from '../../modals/ArchiveProjectModal'
import { userIsEditorOrAdmin } from '../../../workspace/utils'
import { isMyDrafts } from '../../utils'
import { ProjectTrashedDropdown } from './ProjectTrashedDropdown'
import { checkCommonPermissionChecks } from './checkCommonPermissionChecks'
import { ProjectArchivedDropdown } from './ProjectArchivedDropdown'
import { getShowArchiveAction } from '../../../storageItems/utils'

export interface ProjectDropdownProps {
  workspace: WorkspaceMinimalFragment
  project: ProjectFragment | ProjectListItemFragment
  onRename?: () => void
}

const ProjectDropdown = (props: ProjectDropdownProps) => {
  const { workspace, project, onRename } = props
  const workspaceId = workspace.identifier

  const { showModal } = useModalContext()

  const isNestedProjectsOn = useFlag('nested-projects')

  const pinned = project.pinnedByCurrentUserAt
  const isArchived = !!project.archivedAt

  // Only editors and admins can perform the following actions:
  //   - Add/remove people from the project (<ShareProjectAction .../>)
  //   - Rename the project (<RenameProjectAction .../>)
  //   - Delete the project (<DeleteProjectAction .../>)

  const { showDivider, showEditActions, showLeaveAction } =
    checkCommonPermissionChecks(props)
  const showShareAction = showEditActions && project.projectType === 'STANDARD'
  const showArchive = getShowArchiveAction(workspace, project)

  const showPinActions = project.projectType === 'STANDARD' && !isArchived
  const showFolderActions = userIsEditorOrAdmin(workspace) && isNestedProjectsOn
  const showCollectionActions = showEditActions && !isNestedProjectsOn

  const folderTerm = useFolderTerm()

  if (project.deletedAt) {
    return (
      <ProjectTrashedDropdown
        workspace={{ identifier: workspaceId }}
        project={project}
      />
    )
  }

  if (project.archivedAt) {
    return (
      <ProjectArchivedDropdown
        workspace={workspace}
        project={project}
        onRename={onRename}
      />
    )
  }

  return (
    <>
      <Dropdown.Header>{folderTerm.upper}</Dropdown.Header>
      {showShareAction && (
        <ShareProjectAction project={project} workspace={workspace} />
      )}
      <CopyProjectAction project={project} workspaceId={workspaceId} />
      {showDivider && <Dropdown.Divider />}
      {showEditActions && isNestedProjectsOn && (
        <EditProjectAction workspaceId={workspaceId} project={project} />
      )}
      {showEditActions && onRename && (
        <RenameProjectAction onRename={onRename} />
      )}
      {showPinActions && (
        <PinProjectAction
          projectIdentifier={project.identifier}
          pinned={pinned}
          workspaceId={workspaceId}
        />
      )}
      {showFolderActions && (
        <>
          <CreateNestedProjectAction
            project={project}
            workspaceIdentifier={workspaceId}
          />
          {!isMyDrafts(project) && (
            <MoveProjectAction
              project={project}
              workspaceIdentifier={workspaceId}
            />
          )}
        </>
      )}
      {showCollectionActions && (
        <CreateCollectionAction
          project={project}
          workspaceIdentifier={workspaceId}
        />
      )}
      {showDivider && <Dropdown.Divider />}
      {showArchive && (
        <Dropdown.Item
          onClick={() => {
            showModal(ArchiveProjectModal, {
              project,
              workspaceIdentifier: workspaceId,
            })
          }}
        >
          Archive&hellip;
        </Dropdown.Item>
      )}
      {showLeaveAction && (
        <LeaveProjectAction
          workspaceIdentifier={workspaceId}
          projectIdentifier={project.identifier}
          projectName={project.name}
        />
      )}
      {showEditActions && (
        <DeleteProjectAction project={project} workspaceId={workspaceId} />
      )}
    </>
  )
}

export default ProjectDropdown
